'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'

import { cn } from '@/utilities/ui'
import type { Project, Review } from '@/payload-types'
import { NavArrowIcon } from '@/components/icons/NavArrowIcon'
import { getProjectHref } from '@/utilities/projectHref'
import { ReviewVideoPlayer } from '@/components/ReviewsSection/ReviewVideoPlayer'

function ReviewTextClamp({ text, lines = 3 }: { text: string; lines?: number }) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = React.useState(false)
  const [expanded, setExpanded] = React.useState(false)
  const [maxHeight, setMaxHeight] = React.useState<number>(0)

  React.useEffect(() => {
    const el = contentRef.current
    if (!el) return

    // Calculate heights
    const lineHeight = 24 // approximate line height
    const clampedHeight = lineHeight * lines
    const fullHeight = el.scrollHeight

    setMaxHeight(fullHeight)
    setIsOverflowing(fullHeight > clampedHeight)
  }, [text, lines])

  const clampedHeight = lines * 24 // approximate line height

  const containerStyle: React.CSSProperties = {
    maxHeight: expanded ? maxHeight : clampedHeight,
    overflow: 'hidden',
    transition: 'max-height 1.5s ease-in-out',
  }

  return (
    <>
      <div ref={contentRef} style={containerStyle}>
        <p className="leading-normal font-light text-foreground text-[17px]">
          {text}
        </p>
      </div>
      {(isOverflowing || expanded) ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 text-[#009BAB] text-sm font-medium hover:underline"
        >
          {expanded ? 'read less' : 'read more'}
        </button>
      ) : null}
    </>
  )
}


function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" className="h-[26px] w-[26px]" aria-hidden>
      {filled ? (
        <>
          <path d="M923.2 429.6H608l-97.6-304-97.6 304H97.6l256 185.6L256 917.6l256-187.2 256 187.2-100.8-302.4z" fill="#FAD97F" />
          <path d="M1024 396H633.6L512 21.6 390.4 396H0l315.2 230.4-121.6 374.4L512 770.4l316.8 232L707.2 628 1024 396zM512 730.4l-256 187.2 97.6-302.4-256-185.6h315.2l97.6-304 97.6 304h315.2l-256 185.6L768 917.6l-256-187.2z" fill="#F0AD4E" />
        </>
      ) : (
        <path d="M1024 396H633.6L512 21.6 390.4 396H0l315.2 230.4-121.6 374.4L512 770.4l316.8 232L707.2 628 1024 396zM512 730.4l-256 187.2 97.6-302.4-256-185.6h315.2l97.6-304 97.6 304h315.2l-256 185.6L768 917.6l-256-187.2z" fill="#d1d5db" />
      )}
    </svg>
  )
}

function TextStarIcon() {
  return (
    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" aria-hidden>
      <path d="M20.429 8.14c0 0.221-0.16 0.43-0.319 0.589l-4.456 4.346 1.056 6.138c0.012 0.086 0.012 0.16 0.012 0.246 0 0.319-0.147 0.614-0.503 0.614-0.172 0-0.344-0.061-0.491-0.147l-5.512-2.897-5.512 2.897c-0.16 0.086-0.319 0.147-0.491 0.147-0.356 0-0.516-0.295-0.516-0.614 0-0.086 0.012-0.16 0.025-0.246l1.056-6.138-4.469-4.346c-0.147-0.16-0.307-0.368-0.307-0.589 0-0.368 0.381-0.516 0.687-0.565l6.163-0.896 2.762-5.586c0.11-0.233 0.319-0.503 0.602-0.503s0.491 0.27 0.602 0.503l2.762 5.586 6.163 0.896c0.295 0.049 0.688 0.196 0.688 0.565z" fill="rgb(253,211,20)" />
    </svg>
  )
}

function TextStarRating({ rating }: { rating: number }) {
  const n = Math.min(5, Math.max(0, Math.round(rating)))
  return (
    <div className="flex justify-center gap-0.5" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: n }, (_, i) => (
        <TextStarIcon key={i} />
      ))}
    </div>
  )
}

function StarRating({ rating }: { rating: number }) {
  const n = Math.min(5, Math.max(0, Math.round(rating)))
  return (
    <div className="flex justify-center gap-[10px]" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon key={i} filled={i < n} />
      ))}
    </div>
  )
}

function relatedProjectForCta(related: Review['relatedProject']): Pick<Project, 'slug' | 'link'> | null {
  if (typeof related !== 'object' || related === null) return null
  const proj = related as Project
  const slug = typeof proj.slug === 'string' ? proj.slug.trim() : ''
  if (!slug) return null
  return proj
}

function reviewCtaHref(review: Review): string {
  const proj = relatedProjectForCta(review.relatedProject)
  if (proj) return getProjectHref(proj) || '#'
  const linkOverride = typeof review.buttonLink === 'string' ? review.buttonLink.trim() : ''
  return linkOverride || '#'
}

function CtaLink({ href, children }: { href: string; children: React.ReactNode }) {
  const className =
    'inline-flex items-center gap-2 border-2 border-[#009bab] bg-white px-2 py-1 text-[20px] leading-[20px] font-normal text-[#1D1D1B] hover:text-white transition-colors hover:bg-[#009bab] rounded-[3px]'

  if (!href || href === '#') {
    return (
      <span className={cn(className, 'pointer-events-none cursor-default opacity-60')} aria-disabled="true">
        {children}
      </span>
    )
  }

  if (href.startsWith('/')) {
    return <Link className={className} href={href}>{children}</Link>
  }
  return (
    <a className={className} href={href} rel="noopener noreferrer" target="_blank">
      {children}
    </a>
  )
}

function VideoReviewCard({ review }: { review: Review }) {
  const rating = Number.parseInt(String(review.rating), 10) || 0
  return (
    <article className="flex h-full flex-col justify-between gap-[24px] rounded-[9px] bg-white p-5 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)]">
      <div className="flex flex-col items-center text-center gap-[16px]">
        <StarRating rating={rating} />
        <blockquote className="max-w-xl text-left leading-[28px] font-light text-foreground text-[19px]">
          <p>&ldquo;{review.reviewText ?? ''}&rdquo;</p>
        </blockquote>
        <div className="flex flex-col items-center">
          <div className="w-[64px] h-[40px] flex items-center justify-center">
            <Image src="/logo/logo.svg" alt="CRAFT" width={64} height={40} className="w-full h-auto object-contain" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-[20px]">
        <div className=" ">
          <ReviewVideoPlayer review={review} />
        </div>
        <div className="flex justify-center">
          <CtaLink href={reviewCtaHref(review)}>
            {review.buttonText}
            <NavArrowIcon className="h-5 w-5 shrink-0" />
          </CtaLink>
        </div>
      </div>
    </article>
  )
}

function TextReviewCard({ review }: { review: Review }) {
  const rating = Number.parseInt(String(review.rating), 10) || 0
  return (
    <article className="relative h-full rounded-xl border border-border/60 bg-white px-4 py-3 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)]">
      <div className="absolute top-[5px] right-[5px] z-10">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/logo/logo.svg" alt="CRAFT" width={24} height={24} className="h-6 w-6 object-contain" />
      </div>
      <div className="flex flex-col items-center text-center gap-[20px]">
        <TextStarRating rating={rating} />
        <blockquote className="w-full text-left leading-normal font-light text-foreground text-[17px]">
          <ReviewTextClamp text={review.reviewText ?? ''} />
        </blockquote>
        <CtaLink href={reviewCtaHref(review)}>
          {review.buttonText}
          <NavArrowIcon className="h-5 w-5 shrink-0" />
        </CtaLink>
      </div>
    </article>
  )
}

function ReviewCard({ review }: { review: Review }) {
  const hasVideo = Boolean(review.video) || Boolean(review.videoUrl)
  return hasVideo ? <VideoReviewCard review={review} /> : <TextReviewCard review={review} />
}

export function HomepageReviewsSection({
  reviews: reviewsProp,
}: {
  reviews?: unknown
}) {
  const raw = Array.isArray(reviewsProp) ? reviewsProp : []
  const reviews: Review[] = (raw as unknown[])
    .map((r) => (typeof r === 'object' && r !== null ? r : null))
    .filter((r): r is Review => Boolean(r))
    .slice()
    .reverse()

  if (reviews.length === 0) return null

  return (
    <section className="container-fluid mx-auto md:pt-[20px] overflow-hidden ">
      <div className="w-full md:w-[25%]  mb-10 md:mb-[30px]  ml-[20px] md:ml-[30px] ">
        <h2 className="font-medium text-[32px] leading-[38px] tracking-wide" style={{ color: 'rgb(29,29,27)', fontFamily: 'var(--font-outfit-family), sans-serif' }}>
          Our<br />Crafted Relationships
        </h2>
        <div aria-hidden className="h-[10px] bg-[#009bab] w-full md:w-[calc(25vw-25px)]" />
      </div>
      <div className="max-w-[1120px] mx-auto px-[20px] md:px-[30px] 2xl:px-[0px] py-[10px]">
        <div className="grid grid-cols-1 gap-[30px] md:grid-cols-2">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="md:last:odd:col-span-2 md:last:odd:mx-auto md:last:odd:w-[calc(50%-15px)]"
            >
              <ReviewCard review={review} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
